import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useComplaintStore } from "./ComplaintStore/useComplaintStore";
import { normalizeWardIdForPayload } from "../../../Apis/complaintApi";
import ComplaintMapView from "./ComplaintMapView";
import {
  DEFAULT_MAP_CENTER,
  FileUploadField,
  SELECT_MENU_PORTAL_STYLES,
  cancelButtonStyle,
  getDefaultMapCoordinates,
  isValidComplaintImageFile,
  markAllFieldsTouched,
  requiredMark,
  saveButtonStyle,
  sectionHeaderStyle,
} from "./complaintFormShared";

const emptyFormValues = {
  complainantName: "",
  contactNo: "",
  wardId: "",
  streetLocation: "",
  complaintTypeId: "",
  complaintCategoryId: "",
  address: "",
  description: "",
  latitude: "",
  longitude: "",
  zoneId: "",
  complaintType: "Complaint",
  status: "Open",
  modeOfReporting: "Portal",
  complaintCreatedOn: "",
  complaintImageFile: null,
};

const validationSchema = Yup.object().shape({
  complainantName: Yup.string()
    .trim()
    .required("Complainant Name is required")
    .min(3, "Minimum 3 characters required")
    .max(100, "Maximum 100 characters allowed"),
  contactNo: Yup.string()
    .required("Complainant Contact No is required")
    .matches(/^[6-9]\d{9}$/, "Enter a valid 10 digit mobile number"),
  wardId: Yup.string().required("Ward Name is required"),
  streetLocation: Yup.string()
    .trim()
    .required("Street Location is required")
    .max(200, "Maximum 200 characters allowed"),
  complaintTypeId: Yup.string().required("Complaint Type is required"),
  complaintCategoryId: Yup.string().required("Category is required"),
  address: Yup.string()
    .trim()
    .required("Address is required")
    .max(500, "Maximum 500 characters allowed"),
  //description: Yup.string().trim().max(500, "Maximum 500 characters allowed"),
  description: Yup.string()
    .trim()
    .required("Complaint Details is required")
    .min(10, "Minimum 10 characters required")
    .max(1000, "Maximum 1000 characters allowed"),
  // complaintImageFile: Yup.mixed()
  //   .required("Upload File is required")
  //   .test(
  //     "fileType",
  //     "Only JPG, JPEG, and PNG images are allowed",
  //     (file) => isValidComplaintImageFile(file)
  //   ),
  complaintImageFile: Yup.mixed()
    .nullable()
    .test(
      "fileType",
      "Only JPG, JPEG, and PNG images are allowed",
      (file) => !file || isValidComplaintImageFile(file)
    ),
});

const buildAddFormValues = (coordinates = getDefaultMapCoordinates()) => ({
  ...emptyFormValues,
  latitude: coordinates.latitude,
  longitude: coordinates.longitude,
  complaintCreatedOn: new Date().toISOString().slice(0, 16),
});

const AddComplaintForm = ({ showModal, setShowModal, zones, wards }) => {
  const {
    isSaving,
    saveComplaint,
    grievanceCategoriesBySLA,
    slaCategories,
    fetchGrievanceCategoriesBySLA,
    fetchSlaCategories,
  } = useComplaintStore();
  const [locationLoading, setLocationLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("No file chosen");
  const [isChooseFileHovered, setIsChooseFileHovered] = useState(false);
  const formikRef = useRef(null);
  const userPickedLocationRef = useRef(false);
  const wasModalOpenRef = useRef(false);
  const lastSubmittedPayloadRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const justOpened = showModal && !wasModalOpenRef.current;
    const justClosed = !showModal && wasModalOpenRef.current;

    if (justClosed) {
      setMapReady(false);
      formikRef.current?.resetForm({ values: emptyFormValues });
      setSelectedFileName("No file chosen");
      setCurrentLocation(null);
      setIsChooseFileHovered(false);
      userPickedLocationRef.current = false;
    }

    if (justOpened) {
      userPickedLocationRef.current = false;
      formikRef.current?.resetForm({ values: buildAddFormValues() });
      setSelectedFileName("No file chosen");
      useComplaintStore.setState({ grievanceCategoriesBySLA: [] });
      fetchSlaCategories();

      if (navigator.geolocation) {
        setLocationLoading(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const nextLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCurrentLocation(nextLocation);
            setLocationLoading(false);

            if (!userPickedLocationRef.current) {
              const coords = getDefaultMapCoordinates(nextLocation);
              formikRef.current?.setFieldValue("latitude", coords.latitude);
              formikRef.current?.setFieldValue("longitude", coords.longitude);
            }
          },
          () => setLocationLoading(false),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }
    }

    wasModalOpenRef.current = showModal;
  }, [showModal, fetchSlaCategories]);

  useEffect(() => {
    if (!showModal || userPickedLocationRef.current) return;

    const { latitude, longitude } = getDefaultMapCoordinates(currentLocation);
    formikRef.current?.setFieldValue("latitude", latitude);
    formikRef.current?.setFieldValue("longitude", longitude);
  }, [showModal, currentLocation]);

  const handleClose = () => setShowModal(false);

  const getZoneName = (zoneId) =>
    zones.find((zone) => zone.zoneId?.toString() === zoneId?.toString())
      ?.zoneName || "";

  const getWardOptionValue = (ward) =>
    normalizeWardIdForPayload(ward.wardId ?? ward.circleId ?? "");

  const getWardName = (wardId) => {
    const ward = wards.find(
      (item) => getWardOptionValue(item) === normalizeWardIdForPayload(wardId)
    );
    return ward?.wardName || ward?.circleName || ward?.wardNo || "";
  };

  const getZoneIdFromWard = (wardId) => {
    const ward = wards.find(
      (item) => getWardOptionValue(item) === normalizeWardIdForPayload(wardId)
    );
    return ward?.zoneId?.toString() || "";
  };

  const handleSubmit = async (values) => {
    const zoneId = values.zoneId || getZoneIdFromWard(values.wardId);
    const defaultCoordinates = getDefaultMapCoordinates(currentLocation);
    const latitude = values.latitude || defaultCoordinates.latitude;
    const longitude = values.longitude || defaultCoordinates.longitude;
    const wardName = getWardName(values.wardId);

    const payload = {
      complainantName: values.complainantName,
      contactNo: values.contactNo,
      wardId: normalizeWardIdForPayload(values.wardId),
      streetLocation: values.streetLocation,
      wardNo: wardName,
      zoneId,
      zoneName: getZoneName(zoneId),
      revisedStreetLocation: values.streetLocation,
      address: values.address,
      colonyName: values.address || values.streetLocation,
      complaintTypeId: values.complaintTypeId,
      complaintCategoryId: values.complaintCategoryId,
      description: values.description,
      latitude,
      longitude,
      complaintType: values.complaintType,
      status: values.status || "Open",
      modeOfReporting: values.modeOfReporting,
      complaintCreatedOn: values.complaintCreatedOn,
      complaintImageFile: values.complaintImageFile,
    };

    const payloadToCompare = {
      ...payload,
      complaintImageFile: payload.complaintImageFile?.name || null
    };
    const currentPayloadStr = JSON.stringify(payloadToCompare);

    if (lastSubmittedPayloadRef.current === currentPayloadStr) {
      toast.error("Duplicate complaints should not be acceptable.");
      return;
    }

    const result = await saveComplaint(payload, false);
    if (result?.success) {
      lastSubmittedPayloadRef.current = currentPayloadStr;
      handleClose();
    }
  };

  return (
    <Modal
      size="xl"
      show={showModal}
      onHide={handleClose}
      onEntered={() => setMapReady(true)}
      onExited={() => setMapReady(false)}
      mountOnEnter
      unmountOnExit
      enforceFocus={false}
      aria-labelledby="add-complaint-modal-title"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title id="add-complaint-modal-title">Add Complaint</Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") handleClose();
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr className="mt-4" />
      <Modal.Body>
        <Formik
          innerRef={formikRef}
          initialValues={emptyFormValues}
          validationSchema={validationSchema}
          enableReinitialize={false}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, setValues, submitForm, validateForm, setTouched }) => {
            const mapCenter =
              values.latitude && values.longitude
                ? {
                  lat: Number(values.latitude),
                  lng: Number(values.longitude),
                }
                : currentLocation || DEFAULT_MAP_CENTER;

            const handleValidatedSubmit = async () => {
              if (isSaving) return;

              const errors = await validateForm();
              if (errors && Object.keys(errors).length > 0) {
                setTouched(markAllFieldsTouched(emptyFormValues), true);

                const firstErrorKey = Object.keys(errors)[0];
                document
                  .querySelector(`[name="${firstErrorKey}"]`)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
                return;
              }

              await submitForm();
            };

            return (
              <Form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleValidatedSubmit();
                }}
              >
                <h6 className="mb-0" style={sectionHeaderStyle}>
                  Basic Information
                </h6>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label htmlFor="complainantName" className="form-label">
                      Complainant Name {requiredMark}
                    </label>
                    <Field id="complainantName"
                      name="complainantName"
                      className="form-control"
                      placeholder="Select Complainant Name"
                      maxLength={100} onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z\s]/g, "").slice(0, 100);
                        e.target.value = value; setFieldValue("complainantName", value);
                      }} />
                    <ErrorMessage
                      className="required-message"
                      name="complainantName"
                      component="div"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="contactNo" className="form-label">
                      Complainant Contact No (10 Digit No) {requiredMark}
                    </label>
                    <Field
                      id="contactNo"
                      name="contactNo"
                      className="form-control"
                      placeholder="Enter 10 digit mobile no"
                      maxLength={10}
                      onChange={(event) => {
                        const digits = event.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        if (digits.length > 0 && !/^[6-9]/.test(digits)) {
                          return;
                        }
                        setFieldValue("contactNo", digits);
                      }}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="contactNo"
                      component="div"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    {/* <label htmlFor="wardId" className="form-label">
                      Ward Name {requiredMark}
                    </label> */}
                    {/* <label htmlFor="wardId" className="form-label">
                      Ward Name {!values.wardId && requiredMark}

                    </label> */}
                    <label htmlFor="wardId" className="form-label">
                      Ward Name {requiredMark}
                    </label>
                    <Select
                      inputId="wardId"
                      name="wardId"
                      classNamePrefix="react-select"
                      options={wards.map((ward) => ({
                        value: getWardOptionValue(ward),
                        label: ward.wardName || ward.circleName || ward.wardNo,
                      }))}
                      value={
                        wards
                          .map((ward) => ({
                            value: getWardOptionValue(ward),
                            label: ward.wardName || ward.circleName || ward.wardNo,
                          }))
                          .find((option) => option.value === values.wardId) || null
                      }
                      onChange={(selected) => {
                        const wardId = normalizeWardIdForPayload(
                          selected ? selected.value : ""
                        );
                        setValues({
                          ...values,
                          wardId,
                          zoneId: getZoneIdFromWard(wardId),
                        });
                      }}
                      placeholder="Select Ward Name"
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="wardId"
                      component="div"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="streetLocation" className="form-label">
                      Street Location {requiredMark}
                    </label>
                    <Field
                      id="streetLocation"
                      name="streetLocation"
                      className="form-control"
                      placeholder="Enter Street Location"
                    />
                    <ErrorMessage
                      className="required-message"
                      name="streetLocation"
                      component="div"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="complaintTypeId" className="form-label">
                      Complaint Type {requiredMark}
                    </label>
                    <Select
                      inputId="complaintTypeId"
                      name="complaintTypeId"
                      classNamePrefix="react-select"
                      options={slaCategories.map((category) => ({
                        value: String(category.slaCategoryId),
                        label: category.categoryName,
                      }))}
                      value={
                        slaCategories
                          .map((category) => ({
                            value: String(category.slaCategoryId),
                            label: category.categoryName,
                          }))
                          .find((option) => option.value === String(values.complaintTypeId)) ||
                        null
                      }
                      onChange={(selected) => {
                        const val = selected ? selected.value : "";
                        setValues({
                          ...values,
                          complaintTypeId: val,
                          complaintCategoryId: "",
                        });
                        if (val) {
                          fetchGrievanceCategoriesBySLA(val);
                        } else {
                          useComplaintStore.setState({ grievanceCategoriesBySLA: [] });
                        }
                      }}
                      placeholder="Select Complaint Type"
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="complaintTypeId"
                      component="div"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="complaintCategoryId" className="form-label">
                      Complaint Category {requiredMark}
                    </label>
                    <Select
                      inputId="complaintCategoryId"
                      name="complaintCategoryId"
                      classNamePrefix="react-select"
                      options={grievanceCategoriesBySLA.map((category) => ({
                        value: String(category.grievanceCategoryId),
                        label: category.categoryName,
                      }))}
                      value={
                        grievanceCategoriesBySLA
                          .map((category) => ({
                            value: String(category.grievanceCategoryId),
                            label: category.categoryName,
                          }))
                          .find(
                            (option) => option.value === String(values.complaintCategoryId)
                          ) || null
                      }
                      onChange={(selected) =>
                        setFieldValue("complaintCategoryId", selected ? selected.value : "")
                      }
                      placeholder="Select Category"
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="complaintCategoryId"
                      component="div"
                    />
                  </div>

                  {/* <FileUploadField
                    selectedFileName={selectedFileName}
                    setSelectedFileName={setSelectedFileName}
                    isChooseFileHovered={isChooseFileHovered}
                    setIsChooseFileHovered={setIsChooseFileHovered}
                    setFieldValue={setFieldValue}
                  /> */}

                  <FileUploadField
                    selectedFileName={selectedFileName}
                    setSelectedFileName={setSelectedFileName}
                    isChooseFileHovered={isChooseFileHovered}
                    setIsChooseFileHovered={setIsChooseFileHovered}
                    setFieldValue={setFieldValue}
                  />

                  {/* <div className="col-md-12 mb-3">
                    <label htmlFor="address" className="form-label">
                      Address
                    </label>
                    <Field
                      as="textarea"
                      id="address"
                      name="address"
                      rows="3"
                      className="form-control"
                      placeholder="Enter complete address"
                    />
                    <ErrorMessage
                      className="required-message"
                      name="address"
                      component="div"
                    />
                  </div> */}

                  {/* <div className="col-md-12 mb-3">
                    <label htmlFor="address" className="form-label">
                      Address {requiredMark}
                    </label>

                    <Field
                      as="textarea"
                      id="address"
                      name="address"
                      rows="3"
                      className="form-control"
                      placeholder="Enter complete address"
                    />

                    <ErrorMessage
                      className="required-message"
                      name="address"
                      component="div"
                    />
                  </div> */}
                  <div className="col-md-12 mb-3">
                    <label htmlFor="address" className="form-label">
                      Address {requiredMark}
                    </label>

                    <Field
                      as="textarea"
                      id="address"
                      name="address"
                      rows="3"
                      maxLength={500}
                      className="form-control"
                      placeholder="Enter complete address"
                    />

                    <ErrorMessage
                      className="required-message"
                      name="address"
                      component="div"
                    />
                  </div>

                  {/* <div className="col-md-12 mb-3">
                    <label htmlFor="description" className="form-label">
                      Complaint Details
                    </label>
                    <Field
                      as="textarea"
                      id="description"
                      name="description"
                      rows="3"
                      className="form-control"
                      placeholder="Enter complaint details"
                    />
                    <ErrorMessage
                      className="required-message"
                      name="description"
                      component="div"
                    />
                  </div>
                </div> */}

                  <div className="col-md-12 mb-3">
                    <label htmlFor="description" className="form-label">
                      Complaint Details {requiredMark}
                    </label>

                    <Field
                      as="textarea"
                      id="description"
                      name="description"
                      rows="3"
                      className="form-control"
                      placeholder="Enter complaint details"
                    />

                    <ErrorMessage
                      className="required-message"
                      name="description"
                      component="div"
                    />
                  </div>
                </div>
                <h6 className="mb-0 mt-2" style={sectionHeaderStyle}>
                  Map View
                </h6>
                {mapReady ? (
                  <ComplaintMapView
                    latitude={values.latitude}
                    longitude={values.longitude}
                    location={values.streetLocation || values.address}
                    height="320px"
                    showDefaultMap
                    defaultCenter={mapCenter}
                    locationLoading={locationLoading}
                    onLocationSelect={(lat, lng, address) => {
                      userPickedLocationRef.current = true;
                      setFieldValue("latitude", lat);
                      setFieldValue("longitude", lng);
                      if (address) {
                        setFieldValue("address", address);
                      }
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center text-muted"
                    style={{
                      height: "320px",
                      border: "1px solid #E0E0E0",
                      borderRadius: 8,
                      background: "#F8FAFC",
                    }}
                  >
                    {locationLoading
                      ? "Detecting current location..."
                      : "Loading map..."}
                  </div>
                )}

                <hr />
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    style={cancelButtonStyle}
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={saveButtonStyle}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddComplaintForm;
